/* ============================================================
 * frontend-v3/src/screens/automations/AutomationDetail.tsx
 *
 * T316 / V3I-FR-005 — Category A8 decomposition: AutomationDetail
 * Toggle / Edit / Remove.
 *
 * Per PRD §7.1 A8 the surface wires two `useMutationWithToast` instances:
 *
 *   - update: api.automations.update
 *       logLabel:   "automation-update"
 *       mutationName: "automations.update"          (RETRY_SAFE — patch)
 *       successMessage: "Automatización actualizada"
 *
 *   - remove: api.automations.remove
 *       logLabel:   "automation-remove"
 *       mutationName: "automations.remove"          (NOT RETRY_SAFE)
 *       successMessage: "Automatización eliminada"
 *
 * Args contracts (verified at convex/automations.ts:176, 220):
 *   update: { id, name?, description?, trigger?, conditions?, actions? }
 *   remove: { id }
 *
 * Note: the deployed signature uses `id` (not `automationId`); the PRD's
 * "args contract per T-0.0.5" exit-signal is satisfied by the deployed
 * shape — the wiring uses the real arg name.
 *
 * Toggle uses `automations.setEnabled` (already on RETRY_SAFE), routed
 * separately through a third wrapper instance.
 * ============================================================ */
import { useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import type { Id } from '@convex/dataModel';
import {
  Btn, EmptyState, PageHead, Panel, Skeleton, StatCard, Toggle, Input, Field,
} from '../../components/primitives';
import { MockBoundary } from '../../components/Provenance';
import { useMutationWithToast } from '../../hooks/useMutationWithToast';
import { navigate } from '../../lib/nav';
import { ago } from '../../lib/format';

interface AutomationDetailProps {
  id?: string;
}

type AutomationDoc = {
  _id: string;
  name?: string;
  description?: string;
  enabled?: boolean;
  triggerCount?: number;
  runs?: number;
  success?: number;
  lastRanAt?: number;
};

export function AutomationDetail({ id }: AutomationDetailProps) {
  const all = useQuery(api.automations.list, {}) as
    | AutomationDoc[]
    | undefined;
  const auto = all?.find((x) => x._id === id);

  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState<string>('');

  const { run: setEnabled, loading: toggleLoading } = useMutationWithToast(
    api.automations.setEnabled,
    {
      logLabel: 'automation-toggle',
      mutationName: 'automations.setEnabled',
      successMessage: 'Automatización actualizada',
    },
  );

  const { run: updateAutomation, loading: updateLoading } = useMutationWithToast(
    api.automations.update,
    {
      logLabel: 'automation-update',
      mutationName: 'automations.update',
      successMessage: 'Automatización actualizada',
    },
  );

  const { run: removeAutomation, loading: removeLoading } = useMutationWithToast(
    api.automations.remove,
    {
      logLabel: 'automation-remove',
      mutationName: 'automations.remove',
      successMessage: 'Automatización eliminada',
    },
  );

  if (all === undefined) {
    return <Skeleton h={420} />;
  }
  if (auto === undefined) {
    return (
      <EmptyState
        icon="alertTriangle"
        title="Automatización no encontrada"
        body="No se localizó la automatización solicitada."
      />
    );
  }

  const a = auto;

  async function onToggle(next: boolean) {
    try {
      await setEnabled({
        id: a._id as unknown as Id<'automations'>,
        enabled: next,
      });
    } catch {
      // Toast already surfaced.
    }
  }

  async function onSaveEdit() {
    try {
      await updateAutomation({
        id: a._id as unknown as Id<'automations'>,
        name: draftName || (a.name ?? ''),
      });
      setEditing(false);
    } catch {
      // Toast already surfaced.
    }
  }

  async function onRemove() {
    try {
      await removeAutomation({
        id: a._id as unknown as Id<'automations'>,
      });
      navigate('/automations');
    } catch {
      // Toast already surfaced.
    }
  }

  return (
    <div>
      <PageHead
        crumbs={[
          { label: 'Automatizaciones', href: '#/automations' },
          { label: a.name ?? '—' },
        ]}
        title={a.name ?? '—'}
        actions={
          <>
            <Toggle
              on={a.enabled ?? false}
              onChange={(next: boolean) => void onToggle(next)}
              disabled={toggleLoading}
            />
            <Btn
              variant="ghost"
              icon="edit"
              onClick={() => {
                setDraftName(a.name ?? '');
                setEditing((v) => !v);
              }}
            >
              {editing ? 'Cancelar' : 'Editar'}
            </Btn>
            <Btn
              variant="ghost"
              icon="trash"
              loading={removeLoading}
              onClick={() => void onRemove()}
            >
              Eliminar
            </Btn>
            <Btn
              variant="ghost"
              iconRight="fileText"
              onClick={() => navigate('/automations/' + a._id + '/logs')}
            >
              Ver registro
            </Btn>
          </>
        }
      />
      {editing ? (
        <Panel title="Editar automatización">
          <Field label="Nombre">
            <Input value={draftName} onChange={setDraftName} />
          </Field>
          <div style={{ marginTop: 16 }}>
            <Btn
              variant="solid"
              loading={updateLoading}
              onClick={() => void onSaveEdit()}
            >
              Guardar cambios
            </Btn>
          </div>
        </Panel>
      ) : null}
      <div className="circa-grid-4" style={{ marginBottom: 24 }}>
        <StatCard
          label="Ejecuciones totales"
          value={a.triggerCount ?? a.runs ?? 0}
        />
        <StatCard
          label="Tasa de éxito"
          value={
            (a.runs ?? 0) ? (a.success ?? 0) + '%' : '—'
          }
          tone={(a.runs ?? 0) ? 'good' : undefined}
        />
        <StatCard
          label="Última ejecución"
          value={a.lastRanAt ? ago(a.lastRanAt) : '—'}
        />
        <MockBoundary detail="Valor de demostración — la duración media real no se calcula todavía (no hay ejecuciones registradas)." radius={16}>
          <StatCard label="Duración media" value="1.2 s" />
        </MockBoundary>
      </div>
      <Panel title="Ejecuciones recientes" pad={0}>
        <div style={{ padding: 24 }}>
          <EmptyState
            icon="bolt"
            title={(a.runs ?? 0) ? 'Sin registros recientes' : 'Sin ejecuciones'}
            body={
              (a.runs ?? 0)
                ? 'No hay ejecuciones recientes para mostrar.'
                : 'Esta automatización aún no se ha disparado. Actívala para empezar.'
            }
          />
        </div>
      </Panel>
    </div>
  );
}

export default AutomationDetail;
